import LearnLayout from "@/components/LearnLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { TimingList } from "@/components/learn/FeatureCard";
import { FeatureStack } from "@/components/learn/FeatureStack";
import { useState } from "react";
import {
  MessageSquare, MapPin, Truck, Package, QrCode, Barcode, ShieldCheck, CornerDownRight, Wrench,
} from "lucide-react";

const STEPS = [
  { n: "01", title: "List in minutes", desc: "Snap photos, describe it, and set your price. Offer shipping through Sim Racing Connected with a click." },
  { n: "02", title: "Get an offer or a buyer", desc: "Buyers can Message Seller or Buy with protection. We take care of the rest." },
  { n: "03", title: "Ship with SRC", desc: "Order supplies through us if original packaging isn't available. We buy in bulk so you can save. Use our discounted carrier rates and hand off the package." },
];

/* -------- Expandable feature bodies -------- */

function ProtectionBody() {
  const flow = [
    "Seller verifies item and condition",
    "Buyer pays",
    "Payment held in protection",
    "Seller ships",
    "Buyer confirms",
    "Seller paid out",
  ];
  return (
    <div className="grid gap-10 md:grid-cols-2 md:items-center">
      <div>
        <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
          Choose to pay through the platform and enjoy payment protection for the seller and the buyer.
        </p>
        <p className="mt-8 text-[0.86rem] font-semibold uppercase tracking-[0.22em] text-foreground/60">
          Take advantage of our Marketplace system
        </p>
        <TimingList className="mt-4" items={flow} />
        <p className="mt-8 text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
          Something went sideways? Disagreements are reviewed by people who actually know sim gear. Neither side is left holding the loss and scammers cannot win.
        </p>
        <p className="mt-3 text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
          Handling payment in person or outside the platform? No problem, the choice is yours.
        </p>
      </div>

      {/* Mock listing card */}
      <div className="relative flex justify-center">
        <div className="w-full rounded-xl border border-white/20 bg-background/70 p-4 shadow-xl backdrop-blur sm:max-w-[67%]">
          <div className="flex items-center justify-between text-xs">
            <span className="rounded-full border border-primary/60 bg-primary/10 px-2 py-0.5 font-semibold uppercase tracking-wider text-primary">Protected</span>
          </div>
          <div className="relative mt-3 overflow-hidden rounded-md">
            <img src="/banners/momo-sim-rig-portrait.webp" alt="Sim racing rig for sale" className="block w-full h-auto" />
            <span className="absolute left-2 top-2 rounded-full border border-white/25 bg-black/65 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">Excellent</span>
          </div>
          <div className="mt-3 flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-lg font-bold leading-tight">Simucube 2 Pro + GT wheel</p>
              <p className="text-xs text-muted-foreground/60">Excellent Condition · Pick up or Ship</p>
            </div>
          </div>
          <div className="mt-4 rounded-md border border-primary/30 bg-primary/[0.06] px-3 py-2 text-xs">
            <span className="flex items-start gap-2 text-foreground/80">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>Funds held until you<br />confirm delivery</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModeratorBody() {
  return (
    <div>
      <div className="space-y-3">
        {/* You — right */}
        <div className="flex items-start justify-end gap-2">
          <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-secondary/80 px-3 py-2 text-[0.86rem] text-white">
            &quot;Hi, my CS GT wheel arrived with a cracked mount plate which wasn&apos;t shown in the listing. Photos attached.&quot;
          </div>
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-foreground/70">You</div>
        </div>
        {/* SRC — left */}
        <div className="flex items-start gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary">SRC</div>
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-primary/30 bg-primary/[0.06] px-3 py-2 text-[0.86rem] text-white">
            Hi John, sorry to hear that! This is common on the CS GT V1 wheels. We will take care of that for you!
            <span className="mt-1 block text-white/70">Zack, SRC Support</span>
          </div>
        </div>
      </div>
      <p className="mt-8 text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
        Actual sim racers moderate disputes. They know the difference between honest wear and a real fault.
      </p>
    </div>
  );
}

function PickupOrShipBody() {
  const [selected, setSelected] = useState("pickup");
  const pickupActive = selected === "pickup";
  const shipActive = selected === "ship";
  return (
    <div>
      <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
        Sellers offer their preferred delivery, buyers pick at checkout. Meet to pick up in person, or send it across the country.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setSelected("pickup")}
          data-testid="demo-delivery-pickup"
          aria-pressed={pickupActive}
          className={`group/tile relative overflow-hidden rounded-lg border p-4 text-left transition-all duration-300 ${
            pickupActive
              ? "border-primary/60 bg-primary/[0.08] shadow-[0_10px_30px_-20px_hsl(var(--primary)/0.6)]"
              : "border-white/18 bg-background/60 hover:border-primary/40"
          }`}
        >
          <div className="flex items-center justify-between">
            <MapPin className={`h-5 w-5 transition-colors ${pickupActive ? "text-primary" : "text-muted-foreground/70"}`} />
            {pickupActive && (
              <span className="rounded-full border border-primary/50 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary">Selected</span>
            )}
          </div>
          <p className={`mt-3 font-display text-sm font-bold uppercase ${pickupActive ? "text-foreground" : "text-muted-foreground/80"}`}>Local pick up</p>
          <p className={`mt-3 font-display text-lg font-bold ${pickupActive ? "text-primary" : "text-muted-foreground/70"}`}>$0.00</p>
          <svg viewBox="0 0 80 20" className={`mt-3 h-4 w-full transition-colors ${pickupActive ? "text-primary/40" : "text-muted-foreground/30"}`} aria-hidden>
            <path d="M2 10 Q 20 2, 40 10 T 78 10" stroke="currentColor" strokeWidth="1.2" fill="none" strokeDasharray="3 3" />
            <circle cx="2" cy="10" r="2" fill="currentColor" />
            <circle cx="78" cy="10" r="2" fill="currentColor" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => setSelected("ship")}
          data-testid="demo-delivery-ship"
          aria-pressed={shipActive}
          className={`group/tile relative overflow-hidden rounded-lg border p-4 text-left transition-all duration-300 ${
            shipActive
              ? "border-primary/60 bg-primary/[0.08] shadow-[0_10px_30px_-20px_hsl(var(--primary)/0.6)]"
              : "border-white/18 bg-background/60 hover:border-primary/40"
          }`}
        >
          <div className="flex items-center justify-between">
            <Truck className={`h-5 w-5 transition-colors ${shipActive ? "text-primary" : "text-muted-foreground/70"}`} />
            {shipActive && (
              <span className="rounded-full border border-primary/50 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary">Selected</span>
            )}
          </div>
          <p className={`mt-3 font-display text-sm font-bold uppercase ${shipActive ? "text-foreground" : "text-muted-foreground/80"}`}>Shipping</p>
          <p className={`mt-1 text-xs ${shipActive ? "text-foreground/70" : "text-muted-foreground/60"}`}>2–4 business days</p>
          <p className={`mt-3 font-display text-lg font-bold ${shipActive ? "text-primary" : "text-muted-foreground/70"}`}>$14.50</p>
          <p className={`mt-3 text-[10px] uppercase tracking-widest ${shipActive ? "text-foreground/60" : "text-muted-foreground/60"}`}>Discounted carrier rate</p>
        </button>
      </div>
    </div>
  );
}

function LabelsBody() {
  return (
    <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
      <div>
        <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
          Order from pre-selected boxes that are tried and true from shipping thousands of orders and get packing material at checkout. Buyer pays for it only when order is confirmed, seller packages it and sends with a discounted pre-paid label. No hunting for bubble wrap, no last-minute packing supplies runs, no worries about original packaging.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {["Matched Boxes", "Bubble wrap", "Packing Foam", "Pre-paid label"].map((t) => (
            <span key={t} className="rounded-full border border-white/18 bg-background/70 px-3 py-1 text-xs text-foreground/80">
              <Package className="mr-1 inline h-3 w-3 text-primary" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* Mock shipping label */}
      <div className="mx-auto w-full max-w-[408px] rounded-lg border-2 border-dashed border-white/18 bg-background/70 p-5 text-[14px] uppercase tracking-widest">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-muted-foreground/60">Ship to</p>
            <p className="mt-0.5 text-[15px] font-bold normal-case tracking-normal text-foreground/80">John S.</p>
            <p className="mt-0.5 text-[14px] normal-case tracking-normal text-muted-foreground/60">123 Main St., FL</p>
          </div>
          <QrCode className="h-[68px] w-[68px] text-foreground/70" />
        </div>
        <div className="mt-3 flex items-center justify-between border-y border-white/18 py-2">
          <div>
            <p className="text-muted-foreground/60">Service</p>
            <p className="mt-0.5 text-[15px] font-bold normal-case tracking-normal text-foreground/80">Standard</p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground/60">Weight</p>
            <p className="mt-0.5 text-[15px] font-bold normal-case tracking-normal text-foreground/80">4.2 kg</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Barcode className="h-[41px] w-full text-foreground/70" strokeWidth={1.4} />
        </div>
        <p className="mt-1 text-center text-[12px] normal-case tracking-widest text-muted-foreground/60">SRC-2026-A1F284-MK</p>
      </div>
    </div>
  );
}

function ServicesBody() {
  const kinds = [
    "Car setups tuned for you",
    "Custom liveries and team designs",
    "Full rig builds and assembly",
    "One-on-one coaching and driver training",
    "Offering services? Set up your own storefront for free and build your brand",
  ];
  return (
    <div className="grid gap-10 md:grid-cols-2">
      <div>
        <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
          A true marketplace for sim racers, the SRC platform allows you to hire sim racing specialists or post what you are looking for and let them reach out.
        </p>
        <p className="mt-8 text-[0.86rem] font-semibold uppercase tracking-[0.22em] text-foreground/60">
          What you can commission
        </p>
        <TimingList className="mt-4" items={kinds} />
        <p className="mt-8 text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
          Pay a fixed price, an hourly rate, or request a custom quote. All can run through protected checkout for peace of mind.
        </p>
      </div>

      {/* Mock service card */}
      <div className="relative">
        <div className="rounded-xl border border-white/20 bg-background/70 p-4 shadow-xl backdrop-blur">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 rounded-full border border-primary/60 bg-primary/10 px-2 py-0.5 font-semibold uppercase tracking-wider text-primary">
              <Wrench className="h-3 w-3" /> Service
            </span>
            <span className="font-mono text-[10px] text-muted-foreground/60">Storefront · Verified</span>
          </div>
          <div className="mt-3 flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-lg font-bold leading-tight">Pro iRacing GT3 setups</p>
              <p className="text-xs text-muted-foreground/60">Fixed price · 48h turnaround · Remote</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-center text-[11px] font-semibold uppercase tracking-wider">
            <span className="rounded-md border border-primary/30 bg-primary/[0.06] px-2 py-2 text-primary">Buy</span>
            <span className="rounded-md border border-white/15 bg-white/[0.04] px-2 py-2 text-foreground/70">Request quote</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  {
    pos: "01",
    eyebrow: "// PROTECTION",
    title: "Buyer and seller protected on every sale",
    summary: "Payment protection for both the buyer and the seller on every sale.",
    content: <ProtectionBody />,
  },
  {
    pos: "02",
    eyebrow: "// SERVICES",
    title: "Hire sim racing specialists, not just buy gear",
    summary: "Commission car setups, liveries, full rig builds and coaching, or post what you are looking for.",
    content: <ServicesBody />,
  },
  {
    pos: "03",
    eyebrow: "// SUPPORT",
    title: "Reviewed by sim racers, not bots",
    summary: "People who know sim gear moderate every dispute, not bots or general customer support.",
    content: <ModeratorBody />,
  },
  {
    pos: "04",
    eyebrow: "// DELIVERY",
    title: "Pick up or ship",
    summary: "Local pick up or seamless affordable shipping.",
    content: <PickupOrShipBody />,
  },
  {
    pos: "05",
    eyebrow: "// SUPPLIES",
    title: "Boxes, packing, and labels in one click",
    summary: "Boxes, packing materials, and pre-paid labels can be ordered at checkout. No more shopping for shipping material or labels.",
    content: <LabelsBody />,
  },
];

export default function LearnMarketplace() {
  usePageMeta({
    title: "Sim Racing Marketplace | Buy & Sell Gear Safely",
    description:
      "List wheels, pedals, and rigs in minutes. Protected payments, sim-racer support, local pickup or shipping, and labels in one click.",
  });

  return (
    <LearnLayout
      testId="learn-marketplace"
      title="Marketplace"
      sub="From a set of pedals to a full motion rig: buy or list in minutes."
      heroImage="/banners/src-marketplace-banner-2.webp"
      bodyBg="#eceef1"
    >
      <section className="pl-12 sm:pl-14">
        <div className="relative z-20 inline-block -skew-x-[11deg] bg-primary px-4 py-2 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.65)] sm:px-5 sm:py-2.5">
          <h2 className="skew-x-[11deg] font-display text-[1.94rem] font-bold italic uppercase leading-none text-white sm:text-[2.43rem]">
            The first Marketplace built for Sim Racers
          </h2>
        </div>
        <div className="relative z-10 -mt-3">
          <FeatureStack items={FEATURES} testId="marketplace-feature-stack" tabBadge />
        </div>
      </section>

      {/* Shipping made easy — carbon fiber band, full-bleed */}
      <section
        id="shipping-faq"
        className="relative mt-16 overflow-hidden bg-carbon"
        style={{ width: "100vw", marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      >
        <div className="speed-hatch pointer-events-none absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase">
            <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
            Shipping made easy
          </h2>

          <div className="mt-5 space-y-4 pl-10">
            <p className="relative max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
              <CornerDownRight aria-hidden className="absolute -left-10 top-1 h-5 w-5 text-primary" />
              Shipping a motion system or a full rig can sound intimidating. It isn&apos;t with Sim Racing Connected (SRC). We take packaging and shipping off your hands so you can sell your stuff faster.
            </p>
            <p className="relative max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
              <CornerDownRight aria-hidden className="absolute -left-10 top-1 h-5 w-5 text-primary" />
              Whether you need the space, the money, or on to the next upgrade for your rig, we will help you sell it faster. SRC hands you everything you need in a few clicks, with the <strong>buyer paying the shipping cost</strong> at checkout so costs are transparent, helping both buyers and sellers connect faster.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Pre-sized supplies", d: "Boxes, foam and bubble wrap sized for wheels, pedals, bases and rigs — order them at checkout." },
              { t: "Pre-paid labels", d: "Request a discounted, pre-paid shipping label straight from the listing and we will send you one to print. No queueing at the post office or local carrier depot." },
              { t: "Insured, tracked", d: "Every shipment ships insured and tracked end-to-end, protecting both sides if anything happens in transit." },
              { t: "Buyer pays shipping", d: "Shipping cost, supplies and any insurance top-up are billed to the buyer at checkout, line-by-line." },
            ].map((f) => (
              <div key={f.t} className="rounded-lg border border-white/15 bg-background/40 p-4">
                <p className="font-display text-base font-bold uppercase">{f.t}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How the sale flows — stays light grey */}
      <section className="mt-20">
        <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase" style={{ color: "#0b0b0d" }}>
          <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
          How the sale flows
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-lg border border-white/15 bg-card p-6">
              <span className="font-display text-5xl font-bold text-primary/70">{s.n}</span>
              <h3 className="mt-2 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-base" style={{ color: "#c7c8cf" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </LearnLayout>
  );
}
